import React from 'react';
import {
    DocumentTextIcon,
    ClockIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    PlusIcon,
    UserGroupIcon,
    PhoneIcon,
    ChatBubbleLeftRightIcon,
    SpeakerWaveIcon,
    ChartBarIcon,
    ArrowUpIcon,
} from '@heroicons/react/24/outline';

const DashboardDebug = () => {
    // Mock data for testing
    const mockStats = {
        campaigns: {
            total: 15,
            pending: 3,
            processing: 2,
            completed: 10
        },
        numbers: {
            totalNumbers: 5000,
            totalDelivered: 4500,
            totalSent: 4800,
            totalFailed: 300
        },
        campaignTypes: [
            { _id: 'sms', count: 8 },
            { _id: 'whatsapp', count: 5 },
            { _id: 'voice', count: 2 }
        ],
        recentCampaigns: [
            {
                _id: '1',
                title: 'Summer Sale Campaign',
                type: 'sms',
                status: 'completed',
                createdAt: new Date().toISOString(),
                csvFile: { totalNumbers: 1000 }
            },
            {
                _id: '2',
                title: 'Product Launch WhatsApp',
                type: 'whatsapp',
                status: 'processing',
                createdAt: new Date().toISOString(),
                csvFile: { totalNumbers: 500 }
            }
        ]
    };

    const mockUser = {
        name: 'System Admin',
        email: 'admin@campaign.com',
        role: 'admin'
    };

    const campaignTypeIcons = {
        sms: PhoneIcon,
        whatsapp: ChatBubbleLeftRightIcon,
        voice: SpeakerWaveIcon,
    };

    return (
        <div className="space-y-6">
            {/* Debug Info */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h2 className="text-lg font-semibold text-yellow-800 mb-2">🐛 Dashboard Debug Mode</h2>
                <p className="text-yellow-700">This is a debug version of the dashboard with mock data to test the UI components.</p>
            </div>

            {/* Welcome Section */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
                <div className="flex items-center justify-between">
                    <div className="flex-1">
                        <h1 className="text-3xl font-bold mb-2">
                            Welcome back, {mockUser.name}! 👋
                        </h1>
                        <p className="text-white/90 text-lg">
                            Manage campaigns and monitor system activity from your dashboard
                        </p>
                        <div className="mt-4 flex items-center space-x-6 text-white/80">
                            <div className="flex items-center space-x-2">
                                <UserGroupIcon className="h-5 w-5" />
                                <span>Role: {mockUser.role.charAt(0).toUpperCase() + mockUser.role.slice(1)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <ChartBarIcon className="h-5 w-5" />
                                <span>Total Campaigns: {mockStats.campaigns.total}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm font-medium mb-1">
                                Total Campaigns
                            </p>
                            <p className="text-3xl font-bold text-gray-900 mb-2">
                                {mockStats.campaigns.total}
                            </p>
                            <div className="flex items-center text-green-600 text-sm">
                                <ArrowUpIcon className="h-4 w-4 mr-1" />
                                <span>All time</span>
                            </div>
                        </div>
                        <div className="h-14 w-14 bg-blue-100 rounded-xl flex items-center justify-center">
                            <DocumentTextIcon className="h-7 w-7 text-blue-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm font-medium mb-1">
                                Pending
                            </p>
                            <p className="text-3xl font-bold text-gray-900 mb-2">
                                {mockStats.campaigns.pending}
                            </p>
                            <div className="flex items-center text-orange-600 text-sm">
                                <ClockIcon className="h-4 w-4 mr-1" />
                                <span>Awaiting action</span>
                            </div>
                        </div>
                        <div className="h-14 w-14 bg-orange-100 rounded-xl flex items-center justify-center">
                            <ClockIcon className="h-7 w-7 text-orange-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm font-medium mb-1">
                                Completed
                            </p>
                            <p className="text-3xl font-bold text-gray-900 mb-2">
                                {mockStats.campaigns.completed}
                            </p>
                            <div className="flex items-center text-green-600 text-sm">
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                <span>Successfully done</span>
                            </div>
                        </div>
                        <div className="h-14 w-14 bg-green-100 rounded-xl flex items-center justify-center">
                            <CheckCircleIcon className="h-7 w-7 text-green-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-200">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-600 text-sm font-medium mb-1">
                                Processing
                            </p>
                            <p className="text-3xl font-bold text-gray-900 mb-2">
                                {mockStats.campaigns.processing}
                            </p>
                            <div className="flex items-center text-purple-600 text-sm">
                                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                                <span>In progress</span>
                            </div>
                        </div>
                        <div className="h-14 w-14 bg-purple-100 rounded-xl flex items-center justify-center">
                            <ExclamationTriangleIcon className="h-7 w-7 text-purple-600" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Numbers Statistics */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Campaign Performance</h3>
                    <p className="text-gray-600">Overview of your campaign delivery statistics</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 rounded-xl bg-gray-50">
                        <div className="text-3xl font-bold text-gray-900 mb-1">
                            {mockStats.numbers.totalNumbers.toLocaleString()}
                        </div>
                        <div className="text-sm text-gray-600 font-medium">Total Numbers</div>
                    </div>
                    <div className="text-center p-4 rounded-xl bg-green-50">
                        <div className="text-3xl font-bold text-green-700 mb-1">
                            {mockStats.numbers.totalDelivered.toLocaleString()}
                        </div>
                        <div className="text-sm text-green-600 font-medium">Delivered</div>
                    </div>
                    <div className="text-center p-4 rounded-xl bg-blue-50">
                        <div className="text-3xl font-bold text-blue-700 mb-1">
                            {mockStats.numbers.totalSent.toLocaleString()}
                        </div>
                        <div className="text-sm text-blue-600 font-medium">Sent</div>
                    </div>
                    <div className="text-center p-4 rounded-xl bg-red-50">
                        <div className="text-3xl font-bold text-red-700 mb-1">
                            {mockStats.numbers.totalFailed.toLocaleString()}
                        </div>
                        <div className="text-sm text-red-600 font-medium">Failed</div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* Campaign Types Distribution */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Campaign Types</h3>
                        <p className="text-gray-600">Distribution by communication channel</p>
                    </div>
                    <div className="space-y-4">
                        {mockStats.campaignTypes.map((type) => {
                            const Icon = campaignTypeIcons[type._id] || DocumentTextIcon;
                            return (
                                <div key={type._id} className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                                    <div className="flex items-center space-x-4">
                                        <div className="h-12 w-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                                            <Icon className="h-6 w-6 text-white" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900 capitalize text-lg">
                                                {type._id} Campaigns
                                            </p>
                                            <p className="text-sm text-gray-600">Active channel</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-3xl font-bold text-gray-900">
                                            {type.count}
                                        </span>
                                        <div className="text-sm text-gray-600">total</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Recent Campaigns */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="mb-6 flex items-center justify-between">
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Recent Campaigns</h3>
                            <p className="text-gray-600">Your latest campaign activities</p>
                        </div>
                        <button className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium text-sm bg-blue-50 hover:bg-blue-100 px-4 py-2 rounded-lg transition-colors duration-200">
                            View All →
                        </button>
                    </div>
                    <div className="space-y-4">
                        {mockStats.recentCampaigns.map((campaign) => (
                            <div key={campaign._id} className="flex items-center justify-between p-4 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                                <div className="flex items-center space-x-4">
                                    <div className="h-12 w-12 bg-gray-200 rounded-xl flex items-center justify-center">
                                        {React.createElement(campaignTypeIcons[campaign.type] || DocumentTextIcon, {
                                            className: "h-6 w-6 text-gray-600"
                                        })}
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900">
                                            {campaign.title}
                                        </p>
                                        <p className="text-sm text-gray-600">
                                            {campaign.type.toUpperCase()} • {campaign.csvFile.totalNumbers} numbers
                                        </p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${campaign.status === 'completed' ? 'bg-green-100 text-green-700' :
                                        campaign.status === 'processing' ? 'bg-purple-100 text-purple-700' :
                                            campaign.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                                                'bg-red-100 text-red-700'
                                        }`}>
                                        {campaign.status}
                                    </span>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {new Date(campaign.createdAt).toLocaleDateString()}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardDebug; 